import dayjs from "dayjs";
import * as React from "react";

import { toDate, useDateTimeFormat } from "@/shared/datetime";

interface CommentTimeProps {
  /** ISO 8601 timestamp string */
  timestamp: string;

  /** Optional className for styling */
  className?: string;
}

/**
 * Displays a comment timestamp in a human-readable format, with the full
 * second-precision time as its tooltip.
 *
 * - For timestamps within the last 24 hours: shows relative time (e.g., "5 minutes ago", "2 hours ago")
 * - For older timestamps: shows the date and time in the viewer's date and time format
 */
export function CommentTime({ timestamp, className }: CommentTimeProps) {
  const dateTimeFormat = useDateTimeFormat();
  const { displayTime, tooltipTime } = React.useMemo(() => {
    const date = toDate(timestamp);
    if (date === null) {
      return { displayTime: "", tooltipTime: "" };
    }
    const now = dayjs();
    const commentDate = dayjs(date);
    const hoursDiff = now.diff(commentDate, "hours");

    return {
      displayTime:
        hoursDiff < 24
          ? dayjs.duration(commentDate.diff(now)).humanize(true)
          : dateTimeFormat.format(date),
      tooltipTime: dateTimeFormat.format(date, { time: "seconds" }),
    };
  }, [dateTimeFormat, timestamp]);

  return (
    <time dateTime={timestamp} className={className} title={tooltipTime}>
      {displayTime}
    </time>
  );
}
